import {
  afterNextRender,
  Component,
  computed,
  ElementRef,
  inject,
  OnInit,
  signal,
} from "@angular/core";
import { ActivatedRoute, RouterLink } from "@angular/router";
import { SeoService } from "../../../../shared/services/seo.service";
import {
  NavbarComponent,
  NavbarTypeEnum,
} from "../../components/navbar/navbar.component";
import { FooterComponent } from "../../components/footer/footer.component";
import { BlogCardComponent } from "./blog-card.component";
import {
  BreadcrumbComponent,
  BreadcrumbItem,
} from "../../../../shared/ui/breadcrumb/breadcrumb.component";
import { BlogHome, SECTION_LABELS } from "./blog.models";

/**
 * Home del blog (`/blog`): último artículo + destacados por sección + recientes.
 * Data is pre-fetched by blogHomeResolver so it arrives fully loaded on both
 * SSR/prerender and SPA navigation (no client-side fetch flash).
 */
@Component({
  selector: "app-blog-home",
  imports: [
    RouterLink,
    NavbarComponent,
    FooterComponent,
    BlogCardComponent,
    BreadcrumbComponent,
  ],
  host: { "[class.reveals-enabled]": "revealsEnabled()" },
  styles: [
    `
      :host(.reveals-enabled) [data-reveal] {
        opacity: 0;
        transform: translateY(20px);
        transition:
          opacity 0.55s ease-out,
          transform 0.55s ease-out;
      }
      :host(.reveals-enabled) [data-reveal].is-visible {
        opacity: 1;
        transform: none;
      }
      @media (prefers-reduced-motion: reduce) {
        :host(.reveals-enabled) [data-reveal] {
          opacity: 1;
          transform: none;
          transition: none;
        }
      }
    `,
  ],
  template: `
    <app-navbar [type]="navbarTypeEnum.LANDING" [isCheckout]="false" />

    <section
      class="body-font relative flex flex-col items-center justify-center"
    >
      <div
        class="flex flex-wrap justify-center self-center sm:flex-nowrap sm:px-2 sm:py-4"
      >
        <div
          class="relative mx-auto flex overflow-hidden bg-surface shadow-xl sm:rounded-3xl"
        >
          <div class="h-full w-full max-w-7xl">
            <div class="flex h-fit flex-col items-start px-4 py-8 sm:px-16">
              <div class="w-full">
                <!-- Encabezado -->
                <header class="mb-10">
                  <ml-breadcrumb [items]="breadcrumbItems" class="mb-6 block" />
                  <h1 class="text-4xl font-extrabold tracking-tight text-fg">
                    Blog Magrolabs.
                  </h1>
                  <!-- <p class="mt-3 max-w-2xl text-fg-muted">
                    Todo sobre creatina, sin vueltas: guías, recetas, mitos y novedades para
                    entrenar y suplementarte con criterio.
                  </p> -->
                </header>

                @if (home(); as h) {
                  <!-- Último artículo: hero full-width con texto sobre la imagen -->
                  <a
                    [routerLink]="['/', h.latest.section, h.latest.slug]"
                    class="group relative block overflow-hidden rounded-2xl"
                  >
                    <img
                      [src]="h.latest.heroImage.url"
                      [alt]="h.latest.heroImage.alt"
                      class="h-[400px] w-full object-cover transition-transform duration-500 group-hover:scale-105 md:h-[500px]"
                    />
                    <div
                      class="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent"
                    ></div>
                    <div
                      class="absolute inset-x-0 bottom-0 p-6 text-white md:max-w-2xl md:p-8"
                    >
                      <p
                        class="text-xs font-semibold uppercase tracking-wide drop-shadow"
                      >
                        Último artículo · {{ sectionLabels[h.latest.section] }}
                      </p>
                      <h2
                        class="mt-2 text-3xl font-bold leading-tight drop-shadow transition group-hover:underline md:text-4xl"
                      >
                        {{ h.latest.title }}
                      </h2>
                      <p
                        class="mt-3 line-clamp-2 text-base leading-relaxed text-white/90 drop-shadow md:text-lg"
                      >
                        {{ h.latest.excerpt }}
                      </p>
                      <p class="mt-3 text-sm text-white/80 drop-shadow">
                        {{ h.latest.publishedAt }} ·
                        {{ h.latest.readingMinutes }} min · Por
                        {{ h.latest.author.name }}
                      </p>
                    </div>
                  </a>

                  <!-- Destacados: una card por sección, cada una con "Ver todo" -->
                  @if (featuredCards().length > 0) {
                    <section data-reveal class="mt-16">
                      <h2 class="mb-6 text-3xl font-bold text-fg">
                        Destacados
                      </h2>
                      <div class="grid gap-8 sm:grid-cols-2 grid-cols-1">
                        @for (item of featuredCards(); track item.section) {
                          <div>
                            <app-blog-card [post]="item.post" />
                            <a
                              [routerLink]="['/', item.section]"
                              class="mt-3 inline-block text-sm font-medium text-primary transition hover:underline"
                              >Ver todo →</a
                            >
                          </div>
                        }
                      </div>
                    </section>
                  }

                  <!-- Más recientes: de a 5, con "Ver más" -->
                  @if (h.recent.length > 0) {
                    <section data-reveal class="mt-16">
                      <h2 class="mb-6 text-3xl font-bold text-fg">
                        Artículos más recientes
                      </h2>
                      <div class="grid grid-cols-1 gap-20">
                        @for (post of visibleRecent(); track post.slug) {
                          <app-blog-card [post]="post" />
                        }
                      </div>
                      @if (recentLimit() < h.recent.length) {
                        <div class="mt-8 text-center">
                          <button
                            type="button"
                            (click)="showMoreRecent()"
                            class="rounded-full border border-border px-6 py-3 text-sm font-semibold text-fg transition hover:border-primary hover:text-primary"
                          >
                            Ver más
                          </button>
                        </div>
                      }
                    </section>
                  }
                } @else if (empty()) {
                  <div class="py-20 text-center">
                    <h2 class="text-xl font-bold text-fg">
                      Pronto, los primeros artículos
                    </h2>
                    <p class="mt-2 text-fg-muted">
                      Estamos preparando guías, recetas y novedades sobre
                      creatina.
                    </p>
                    <a
                      routerLink="/productos"
                      class="mt-6 inline-block rounded-full bg-primary px-6 py-3 text-sm font-semibold text-white transition hover:bg-primary-hover"
                      >Ver la creatina</a
                    >
                  </div>
                } @else if (error()) {
                  <div class="py-20 text-center">
                    <h2 class="text-xl font-bold text-fg">
                      No pudimos cargar el blog
                    </h2>
                    <p class="mt-2 text-fg-muted">
                      Probá de nuevo en un momento.
                    </p>
                  </div>
                } @else {
                  <!-- Skeleton -->
                  <div
                    class="grid animate-pulse gap-6 sm:grid-cols-2"
                    aria-hidden="true"
                  >
                    <div class="aspect-[16/10] rounded-2xl bg-bg-alt"></div>
                    <div class="space-y-3 py-4">
                      <div class="h-4 w-24 rounded bg-bg-alt"></div>
                      <div class="h-8 w-3/4 rounded bg-bg-alt"></div>
                      <div class="h-4 w-full rounded bg-bg-alt"></div>
                      <div class="h-4 w-2/3 rounded bg-bg-alt"></div>
                    </div>
                  </div>
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <app-footer />
  `,
})
export class BlogHomeComponent implements OnInit {
  protected readonly navbarTypeEnum = NavbarTypeEnum;
  protected readonly sectionLabels = SECTION_LABELS;
  protected readonly home = signal<BlogHome | null>(null);
  protected readonly empty = signal(false);
  protected readonly error = signal(false);
  protected readonly revealsEnabled = signal(false);

  /** Client-side pagination for "more recents": 5 per page. */
  protected readonly recentLimit = signal(5);
  protected readonly visibleRecent = computed(
    () => this.home()?.recent.slice(0, this.recentLimit()) ?? [],
  );

  /** One representative card per section with posts (Destacados grid). */
  protected readonly featuredCards = computed(() =>
    (this.home()?.featured ?? [])
      .filter((group) => group.posts.length > 0)
      .map((group) => ({ section: group.section, post: group.posts[0] })),
  );

  /** Breadcrumb for the blog home (shared ml-breadcrumb primitive). */
  protected readonly breadcrumbItems: BreadcrumbItem[] = [
    { label: "Inicio", link: "/" },
    { label: "Blog" },
  ];

  private readonly route = inject(ActivatedRoute);
  private readonly seo = inject(SeoService);
  private readonly hostEl = inject(ElementRef);
  private observer?: IntersectionObserver;

  constructor() {
    afterNextRender(() => {
      this.revealsEnabled.set(true);
      if ("IntersectionObserver" in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (entry.isIntersecting) {
                entry.target.classList.add("is-visible");
                this.observer?.unobserve(entry.target);
              }
            }
          },
          { threshold: 0, rootMargin: "0px 0px -8% 0px" },
        );
      }
      this.observeReveals();
    });
  }

  ngOnInit(): void {
    // SEO runs first — data is already available from the resolver.
    this.seo.setTitle("Blog de creatina | Magrolabs");
    this.seo.setDescription(
      "Guías, recetas, mitos y novedades sobre creatina monohidratada para entrenar y suplementarte con criterio.",
    );
    this.seo.setCanonicalURL("https://magrolabs.com/blog");
    this.seo.setIndexFollow(true);
    this.seo.setStructuredData(
      {
        "@context": "https://schema.org",
        "@type": "Blog",
        name: "Blog de Magrolabs",
        url: "https://magrolabs.com/blog",
        description:
          "Contenido sobre creatina: guías, recetas, mitos y novedades.",
      },
      "ld-blog",
    );

    // Read resolved data — resolver guarantees this runs before the component renders.
    const resolved = this.route.snapshot.data["blogHome"] as BlogHome | null;
    if (resolved === null) {
      this.error.set(true);
    } else {
      this.home.set(resolved);
      if (
        !resolved.latest &&
        (!resolved.recent || resolved.recent.length === 0)
      ) {
        this.empty.set(true);
      }
      this.scheduleReveals();
    }
  }

  /**
   * Schedules the IntersectionObserver setup after the DOM renders.
   * On SPA navigation data arrives synchronously (from resolver), so we wait
   * one double-rAF to ensure sections are already in the DOM before observing.
   */
  private scheduleReveals(): void {
    if (typeof requestAnimationFrame === "undefined") return; // SSR
    requestAnimationFrame(() =>
      requestAnimationFrame(() => this.observeReveals()),
    );
  }

  /** Observes all not-yet-revealed sections (re-callable when data arrives). */
  private observeReveals(): void {
    const root = this.hostEl.nativeElement as HTMLElement;
    const nodes = root.querySelectorAll<HTMLElement>(
      "[data-reveal]:not(.is-visible)",
    );
    if (!this.observer) {
      nodes.forEach((node) => node.classList.add("is-visible"));
      return;
    }
    nodes.forEach((node) => this.observer!.observe(node));
  }

  protected showMoreRecent(): void {
    this.recentLimit.update((n) => n + 5);
  }
}
